import type { Option } from "../core/option.ts";
import { UserId } from "./ids.ts";
import { SheetId } from "./sheet-id.ts";

/**
 * The plain form of a sheet key (`SheetKey.toRef()`): a sheet id, or a
 * lookup key with its user — what a sheet event (`SheetMetadataChanged`,
 * `SheetWidthChanged`) names when the sheet may be known only by its key.
 */
export type SheetKeyRef =
  | {
    readonly kind: "sheetId";
    readonly sheetId: SheetId;
  }
  | {
    readonly kind: "lookup";
    /** The `:key` lookup key. */
    readonly lookupKey: string;
    readonly userId: Option<UserId>;
  };

/** Readers of a `SheetKeyRef`: `parse`, `names`, `equals`, `pathOf`. */
export namespace SheetKeyRef {
  /** The ref of the sheet `id`. */
  export function fromId(id: SheetId): SheetKeyRef {
    return { kind: "sheetId", sheetId: id };
  }

  /**
   * `raw`, a sheet key in its wire spelling: the id it names when it is one
   * (`ss_…`, a UUID, `ephemeral(…)`), else the lookup key it is —
   * `[user]:lookup` as that user's, any other text as the key itself.
   */
  export function of(raw: string): SheetKeyRef {
    const sheetId = SheetId.parse(raw);
    if (sheetId != null) return fromId(sheetId);
    const scoped = /^\[([^\]]+)\](:?.+)$/.exec(raw);
    if (scoped != null) {
      const key = scoped[2] ?? "";
      return {
        kind: "lookup",
        lookupKey: key.startsWith(":") ? key : `:${key}`,
        userId: UserId.parse(scoped[1] ?? ""),
      };
    }
    return { kind: "lookup", lookupKey: raw, userId: null };
  }

  /** Whether `ref` names the sheet `id` (a lookup-key ref names no id). */
  export function names(ref: SheetKeyRef, id: SheetId): boolean {
    return ref.kind === "sheetId" && ref.sheetId.equals(id);
  }

  /** Whether `a` and `b` are the same ref. */
  export function equals(a: SheetKeyRef, b: SheetKeyRef): boolean {
    if (a.kind === "sheetId") return b.kind === "sheetId" && a.sheetId.equals(b.sheetId);
    return b.kind === "lookup" && a.lookupKey === b.lookupKey && a.userId === b.userId;
  }

  /** `ref` as an invalidation path segment: the id, or `[user]:lookup` / `:lookup`. */
  export function pathOf(ref: SheetKeyRef): string {
    if (ref.kind === "sheetId") return ref.sheetId.toString();
    return ref.userId != null ? `[${ref.userId}]${ref.lookupKey}` : ref.lookupKey;
  }
}
